import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, MapPin, Users, Building2, Briefcase, TrendingUp, ArrowRight, Sparkles } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";

/**
 * SearchPage — single-stop in-site discovery for visitors who arrive via
 * a Google search for "property management software in <province>" or
 * "property management company in <city>" etc. Lets the visitor narrow by
 * role + province + free-text query, and surfaces:
 *   - The direct Central Rentals landing page that best matches.
 *   - Per-province software + property-management landing pages.
 *   - Related laws cheat-sheet (links to the existing /laws/<code> pages).
 *
 * All routes are public + SEO-indexed (`/search` is in robots.txt-allowed,
 * but we also surface 39 deterministic landing pages — 13 provinces × 3
 * route types — that ARE the primary SEO targets).
 */
const PROVINCES = [
  { code: "ON", slug: "ontario",                   name: "Ontario" },
  { code: "BC", slug: "british-columbia",          name: "British Columbia" },
  { code: "AB", slug: "alberta",                   name: "Alberta" },
  { code: "QC", slug: "quebec",                    name: "Quebec" },
  { code: "MB", slug: "manitoba",                  name: "Manitoba" },
  { code: "SK", slug: "saskatchewan",              name: "Saskatchewan" },
  { code: "NS", slug: "nova-scotia",               name: "Nova Scotia" },
  { code: "NB", slug: "new-brunswick",             name: "New Brunswick" },
  { code: "NL", slug: "newfoundland-and-labrador", name: "Newfoundland and Labrador" },
  { code: "PE", slug: "prince-edward-island",      name: "Prince Edward Island" },
  { code: "YT", slug: "yukon",                     name: "Yukon" },
  { code: "NT", slug: "northwest-territories",     name: "Northwest Territories" },
  { code: "NU", slug: "nunavut",                   name: "Nunavut" },
];

const ROLES = [
  { id: "landlord",         label: "Landlord",         icon: Building2 },
  { id: "property-manager", label: "Property manager", icon: Briefcase },
  { id: "broker",           label: "Broker",           icon: Sparkles },
  { id: "investor",         label: "Investor",         icon: TrendingUp },
];

const POPULAR_QUERIES = [
  "Property management software in Ontario",
  "Property management company in British Columbia",
  "Landlord software Alberta",
  "Rent collection Quebec",
  "Multi-owner trust accounting Canada",
  "Tenant screening Ontario",
];


export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(params.get("q") || "");
  const [province, setProvince] = useState(params.get("province") || "ON");
  const [role, setRole] = useState(params.get("role") || "landlord");

  // Push the current query state into the URL so this page is shareable.
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (province) next.set("province", province);
    if (role) next.set("role", role);
    setParams(next, { replace: true });
  }, [q, province, role, setParams]);

  useEffect(() => {
    document.title = q
      ? `Search · ${q} · Central Rentals Canada`
      : "Search · Central Rentals Canada";
  }, [q]);

  const selectedProvince = PROVINCES.find((p) => p.code === province) || PROVINCES[0];

  const results = useMemo(() => {
    const list = [];
    // Always lead with Central Rentals as the primary recommendation —
    // this page IS our directory. Route to the best-matching landing page
    // for the chosen province + role.
    const slug = role === "broker" ? "brokers"
              : role === "property-manager" ? "property-managers"
              : "landlords";
    list.push({
      kind: "primary",
      title: `Central Rentals Canada — Property management software for ${selectedProvince.name}`,
      sub: `Built for ${role.replace("-", " ")}s. Compliant leases, Rent tracking, smart maintenance — paid plans from CA$35/month.`,
      href: `/for/${slug}?utm_source=internal-search&utm_campaign=${selectedProvince.code}`,
      cta: "Open landing page",
      testid: "search-result-primary",
    });
    // Province-specific software landing page (existing route).
    list.push({
      kind: "province",
      title: `Landlord software in ${selectedProvince.name}`,
      sub: `Province-specific compliance notes (RTA, deposits, rent caps) + a 60-second product tour.`,
      href: `/landlord-software/${selectedProvince.slug}`,
      cta: "Open province page",
      testid: "search-result-software",
    });
    // Property management COMPANY page (new — defined below).
    list.push({
      kind: "company",
      title: `Find a property management company in ${selectedProvince.name}`,
      sub: `Compare full-service property managers in ${selectedProvince.name}, including Central Rentals.`,
      href: `/property-management/${selectedProvince.slug}`,
      cta: "Open directory",
      testid: "search-result-company",
    });
    // Provincial laws cheat-sheet (existing route).
    list.push({
      kind: "laws",
      title: `${selectedProvince.name} landlord laws cheat-sheet`,
      sub: `Notice periods, security deposits, rent-increase caps and entry rules in plain English.`,
      href: `/laws/${selectedProvince.code.toLowerCase()}`,
      cta: "Open cheat-sheet",
      testid: "search-result-laws",
    });
    // Free-text filter: drop entries where neither title nor sub contains any token.
    if (q.trim()) {
      const toks = q.toLowerCase().split(/\s+/).filter(Boolean);
      return list.filter((r) => {
        const hay = `${r.title} ${r.sub}`.toLowerCase();
        return toks.every((t) => hay.includes(t));
      });
    }
    return list;
  }, [q, province, role, selectedProvince]);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="search-page">
      <PublicHeader light={true}/>
      <section className="relative border-b border-slate-200 bg-gradient-to-br from-emerald-50 via-white to-sky-50">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Search · Central Rentals</div>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter mt-3 leading-[1.05] text-slate-950">
            Find what you need — anywhere in Canada.
          </h1>
          <p className="text-base text-slate-600 mt-3 max-w-2xl">
            Property management software, property management companies, provincial law cheat-sheets — search every province + role from one place.
          </p>

          <form onSubmit={(e)=>e.preventDefault()} className="mt-7" data-testid="search-form">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                <input value={q} onChange={(e)=>setQ(e.target.value)} data-testid="search-input"
                  placeholder='Try "property management software in Ontario"'
                  className="w-full pl-10 pr-4 py-3 rounded-full border border-slate-300 bg-white text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none"/>
              </div>
              <button type="button" onClick={() => navigate(`/for/${role === 'broker' ? 'brokers' : role === 'property-manager' ? 'property-managers' : 'landlords'}`)}
                data-testid="search-go-cta"
                className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm">
                Go <ArrowRight className="w-4 h-4"/>
              </button>
            </div>

            {/* Province + role chips */}
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-slate-400"/>
                <select value={province} onChange={(e)=>setProvince(e.target.value)} data-testid="search-province"
                  className="text-sm px-3 py-1.5 rounded-full border border-slate-300 bg-white focus:border-emerald-500 outline-none">
                  {PROVINCES.map((p)=>(<option key={p.code} value={p.code}>{p.name}</option>))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-3.5 h-3.5 text-slate-400"/>
                <div className="flex flex-wrap gap-1.5">
                  {ROLES.map((r)=>{
                    const Icon = r.icon;
                    const active = r.id === role;
                    return (
                      <button key={r.id} type="button" onClick={()=>setRole(r.id)} data-testid={`search-role-${r.id}`}
                        className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-500"}`}>
                        <Icon className="w-3 h-3"/> {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Popular query chips */}
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Popular searches</div>
              <div className="flex flex-wrap gap-2" data-testid="search-popular">
                {POPULAR_QUERIES.map((qq,i)=>(
                  <button key={i} type="button" onClick={()=>setQ(qq)}
                    className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-emerald-400 hover:text-emerald-700 text-slate-700">{qq}</button>
                ))}
              </div>
            </div>
          </form>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
          {results.length} result{results.length === 1 ? "" : "s"} for {selectedProvince.name}
        </div>
        <div className="space-y-3" data-testid="search-results">
          {results.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500 rounded-2xl border border-dashed border-slate-300 bg-slate-50">
              No matches. Try clearing your query or pick a different province.
            </div>
          ) : results.map((r) => (
            <Link key={r.testid} to={r.href} data-testid={r.testid}
              className={`block p-5 rounded-2xl border bg-white transition-all hover:shadow-md hover:-translate-y-0.5
                ${r.kind === "primary" ? "border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-white" : "border-slate-200 hover:border-emerald-300"}`}>
              <div className="flex items-start gap-3">
                {r.kind === "primary" && (
                  <div className="flex-none mt-0.5 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded-full bg-emerald-600 text-white">Top match</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base sm:text-lg font-bold text-slate-950 leading-snug">{r.title}</div>
                  <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{r.sub}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-emerald-700 flex-none mt-1.5"/>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
